<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <v-card>
          <v-toolbar :dark="$vuetify.theme.dark" color="primary">
            <v-col class="text-left">
              <v-toolbar-title class="textwithbackground--text">
                {{ $t('Список организаций') }}
              </v-toolbar-title>
            </v-col>
          </v-toolbar>
          <general-error v-if="!!generalError && !editOrganizationDialog.show" />
          <v-row class="px-2 pt-2">
            <v-col>
              <v-expansion-panels>
                <v-expansion-panel v-test="{ panel: '' }">
                  <v-expansion-panel-header>
                    <div id="search">
                      <v-icon>
                        search
                      </v-icon>
                      {{ $t('Фильтры') }}
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content eager @keydown.native.enter="focusOnBtn('searchBtn')">
                    <v-row>
                      <v-col
                          class="pl-3"
                          cols="12"
                          sm="10"
                          md="4"
                          xl="3">
                        <v-text-field
                            v-model="params.organizationName"
                            v-test="{ input: 'organizationName' }"
                            clearable
                            :label="$t('Наименование организации')"
                            hide-details
                            @keydown.native.enter="focusOnBtn('searchBtn')" />
                      </v-col>
                      <v-col
                          class="pl-3"
                          cols="12"
                          sm="10"
                          md="4"
                          xl="3">
                        <v-text-field
                            v-model="params.inn"
                            v-test="{ input: 'inn' }"
                            clearable
                            :label="$t('ИНН')"
                            hide-details
                            @keydown.native.enter="focusOnBtn('searchBtn')" />
                      </v-col>
                      <v-col
                          class="pl-3"
                          cols="12"
                          sm="10"
                          md="4"
                          xl="3">
                        <v-text-field
                            v-model="params.ogrn"
                            v-test="{ input: 'ogrn' }"
                            clearable
                            :label="$t('ОГРН')"
                            hide-details
                            @keydown.native.enter="focusOnBtn('searchBtn')" />
                      </v-col>
                      <v-col
                          class="pl-3"
                          cols="12"
                          sm="10"
                          md="4"
                          xl="3">
                        <validatable-enum-combobox
                            v-model="params.participantType"
                            v-test="{ combobox: 'participantType' }"
                            clearable
                            :values-enum="PARTICIPANT_TYPE_ENUM"
                            :label="$t('Категория участия')"
                            @change="focusOnBtn('searchBtn')" />
                      </v-col>
                      <v-col
                          class="pl-3"
                          cols="12"
                          sm="10"
                          md="4"
                          xl="3" />
                      <v-col
                          class="text-right pr-2"
                          align-self="center">
                        <btn-icon-tip
                            id="search"
                            ref="searchBtn"
                            icon="search"
                            icon-color="primary"
                            :tip="$t('Поиск')"
                            @click="search" />
                      </v-col>
                    </v-row>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col>
          </v-row>
          <v-row
              v-if="isAdmin"
              justify="end"
              class="px-2">
            <v-col
                class="text-right"
                cols="12">
              <btn-icon-tip
                  id="sync"
                  icon="sync"
                  icon-color="info"
                  :tip="$t('Синхронизировать')"
                  :disabled="isLoading"
                  @click="synchronizeOrganizations" />
            </v-col>
          </v-row>
          <v-row class="px-2">
            <v-col>
              <paginated-data-table
                  v-if="getOrganizationsList"
                  ref="paginatedTable"
                  :headers="headers"
                  :items="organizations"
                  :loading="isLoading"
                  item-key="organizationId"
                  :total-count="totalCount"
                  :items-per-page="itemsPerPage"
                  show-expand
                  :request-method="getOrganizationsList">
                <!-- eslint-disable-next-line vue/valid-v-slot -->
                <template v-slot:item.organizationShortName="{ item }">
                  <route-link-with-tip
                      v-if="isAdmin"
                      :id="item.organizationShortName"
                      :text="item.organizationShortName"
                      :tip="$t('Просмотр организации')"
                      :to="{ path: `${urlRootPath}/organization/${item.organizationId}` }" />
                  <span v-else>
                    {{ item.organizationShortName }}
                  </span>
                </template>
                <!-- eslint-disable-next-line vue/valid-v-slot -->
                <template v-slot:item.inn="{ item }">
                  {{ item.inn }}
                </template>
                <!-- eslint-disable-next-line vue/valid-v-slot -->
                <template v-slot:item.contactPerson="{ item }">
                  {{ item.contactPerson }}
                </template>
                <!-- eslint-disable-next-line vue/valid-v-slot -->
                <template v-slot:item.phone="{ item }">
                  {{ item.phone }}
                </template>
                <!-- eslint-disable-next-line vue/valid-v-slot -->
                <template v-slot:item.participantType="{ item }">
                  {{ $t(PARTICIPANT_TYPE_ENUM[item.participantType]) }}
                </template>
                <!-- eslint-disable-next-line vue/valid-v-slot -->
                <template v-slot:item.organizationType="{ item }">
                  {{ $t(ORGANIZATION_TYPE_ENUM[item.organizationType]) }}
                </template>
                <!-- eslint-disable-next-line vue/valid-v-slot -->
                <template v-slot:item.isLocked="{ item }">
                  <btn-icon-tip
                      v-if="isAdmin"
                      :id="`lock-${item.organizationId}`"
                      :icon="item.isLocked
                        ? 'lock'
                        : 'lock_open'"
                      :icon-color="item.isLocked
                        ? 'error'
                        : 'success'"
                      :tip="$t('Изменить статус. Текущий: {0}', [ $t(item.isLocked
                        ? 'Заблокирован'
                        : 'Активный') ] )"
                      @click="openStatusDialog(item.organizationId,
                                               item.isLocked)" />
                  <icon-tip
                      v-else
                      :icon="item.isLocked
                        ? 'lock'
                        : 'lock_open'"
                      :icon-color="item.isLocked
                        ? 'error'
                        : 'success'"
                      :tip="$t('Текущий статус: {0}', [ $t(item.isLocked
                        ? 'Заблокирован'
                        : 'Активный') ] )" />
                </template>
                <template v-slot:expanded-item="{ item }">
                  <td :colspan="headers.length" class="text-left py-2">
                    <v-row class="text-left my-2">
                      <v-col>
                        <v-row v-if="item.contractNumber" class="mb-2">
                          <text-view
                              v-if="item.contractNumber"
                              :display="item.contractNumber"
                              :label="$t('Номер договора')" />
                        </v-row>
                        <v-divider />
                        <btn-icon-tip
                            v-if="isAdmin"
                            :id="`editOrganization-${item.organizationId}`"
                            icon="edit"
                            :tip="$t('Редактировать организацию')"
                            @click="openEditOrganizationDialog(item.organizationId)" />
                        <btn-icon-tip
                            v-if="isAdmin"
                            :id="`getRegCard-${item.organizationId}`"
                            icon="description"
                            :tip="$t('Скачать регистрационную карточку')"
                            @click="getRegistrationCard(item.organizationId)" />
                      </v-col>
                    </v-row>
                  </td>
                </template>
              </paginated-data-table>
            </v-col>
          </v-row>
          <confirm-dialog
              v-if="confirmDialog.show"
              :dialog-prop="confirmDialog"
              @close="closeDialog('confirmDialog')" />
          <edit-organization-dialog
              v-if="editOrganizationDialog.show"
              :dialog-prop="editOrganizationDialog"
              @close="closeDialog('editOrganizationDialog')" />
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import BtnIconTip from '@/components/reused/ui/BtnIconTip.vue'
  import IconTip from '@/components/reused/ui/IconTip.vue'
  import TextView from '@/components/reused/ui/TextView.vue'
  import RouteLinkWithTip from '@/components/reused/ui/RouteLinkWithTip.vue'
  import ValidatableEnumCombobox from '@/components/reused/ui/ValidatableEnumCombobox.vue'
  import EditOrganizationDialog from '@/components/pages/admin/Organizations/EditOrganizationDialog.vue'
  import PaginatedDataTable from '@/components/reused/ui/PaginatedDataTable.vue'
  import ConfirmDialog from '@/components/reused/common/ConfirmDialog.vue'
  import GeneralError from '@/components/reused/common/GeneralError.vue'
  import { PARTICIPANT_TYPE_ENUM, ORGANIZATION_TYPE_ENUM } from '@/constants/enums'
  import { mapActions, mapState } from 'vuex'
  import { ORGANIZATION_MANAGEMENT } from '@/constants/store.js'

  export default {
    components: {
      BtnIconTip,
      IconTip,
      TextView,
      EditOrganizationDialog,
      RouteLinkWithTip,
      ValidatableEnumCombobox,
      ConfirmDialog,
      GeneralError,
      PaginatedDataTable
    },
    data () {
      return {
        PARTICIPANT_TYPE_ENUM,
        ORGANIZATION_TYPE_ENUM,
        isLoading: false,
        itemsPerPage: 10,
        confirmDialog: {
          show: false,
          additional: null,
          text: null,
          callback: null,
          warning: true
        },
        editOrganizationDialog: {
          show: false,
          organizationId: null,
          callback: null
        },
        params: {
          organizationName: '',
          participantType: '',
          inn: '',
          ogrn: ''
        }
      }
    },
    computed: {
      ...mapState(ORGANIZATION_MANAGEMENT.NAMESPACE, {
        organizationsInfo: state => (state[ORGANIZATION_MANAGEMENT.STATE.ORGANIZATIONS] || {})
      }),
      headers () {
        const headers = [
          { text: this.$t('Организация'), align: 'start', sortable: true, value: 'organizationShortName' },
          { text: this.$t('ИНН'), align: 'start', sortable: false, value: 'inn' },
          { text: this.$t('ОГРН'), align: 'start', sortable: false, value: 'ogrn' },
          { text: this.$t('Контактное лицо (ФИО)'), align: 'start', sortable: false, value: 'contactPerson' },
          { text: this.$t('Телефон'), align: 'start', sortable: false, value: 'phone' },
          { text: this.$t('Категория участия'), align: 'start', sortable: true, value: 'participantType' },
          { text: this.$t('Форма организации'), align: 'start', sortable: true, value: 'organizationType' },
          { text: this.$t('Статус блокировки'), align: 'center', sortable: false, value: 'isLocked' },
          { text: '', value: 'data-table-expand' }
        ]
        // if (!this.isAdmin) {
        //   headers = headers.filter(h => h.value !== 'data-table-expand')
        // }
        return headers
      },
      totalCount () {
        return this.organizationsInfo.totalCount || 0
      },
      organizations () {
        return this.organizationsInfo.items || []
      }
    },
    created () {
      this.clearStore()
    },
    beforeDestroy () {
      this.clearStore()
    },
    methods: {
      ...mapActions(ORGANIZATION_MANAGEMENT.NAMESPACE, {
        getRegistrationCardAction: ORGANIZATION_MANAGEMENT.ACTIONS.GET_REGISTRATION_CARD,
        getOrganizationsListAction: ORGANIZATION_MANAGEMENT.ACTIONS.GET_ORGANIZATIONS_LIST,
        changeOrganizationLockStatusAction: ORGANIZATION_MANAGEMENT.ACTIONS.CHANGE_ORGANIZATION_LOCK_STATUS,
        clearOrganizationsListAction: ORGANIZATION_MANAGEMENT.ACTIONS.CLEAR_ORGANIZATIONS_LIST,
        synchronizeOrganizationsAction: ORGANIZATION_MANAGEMENT.ACTIONS.SYNCHRONIZE_ORGANIZATIONS
      }),
      clearStore: async function () {
        await Promise.all(
          [
            this.clearOrganizationsListAction()
          ])
      },
      search: async function () {
        await this.$refs.paginatedTable.resetPaginationAndGetDataFromServer()
      },
      getOrganizationsList: async function (params) {
        try {
          this.isLoading = true
          await this.getOrganizationsListAction({
            ...params,
            ...this.params
          })
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      openEditOrganizationDialog: async function (organizationId) {
        this.editOrganizationDialog.organizationId = organizationId
        this.editOrganizationDialog.callback = this.$refs.paginatedTable.getDataFromServer
        this.editOrganizationDialog.show = true
      },
      openStatusDialog: function (organizationId, isLocked) {
        this.confirmDialog.additional = {
          isLocked: !isLocked,
          organizationId
        }
        const action = this.$t(isLocked
          ? 'разблокирована'
          : 'заблокирована')
        this.confirmDialog.text = this.$t('Организация будет {0}. Продолжить?', [action])
        this.confirmDialog.callback = this.changeOrganizationLockStatus
        this.confirmDialog.warning = true
        this.confirmDialog.show = true
      },
      changeOrganizationLockStatus: async function (status) {
        try {
          this.isLoading = true
          await this.changeOrganizationLockStatusAction(status)
          this.isLoading = false
          this.$refs.paginatedTable.getDataFromServer()
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      getRegistrationCard: async function (organizationId) {
        try {
          this.isLoading = true
          await this.getRegistrationCardAction({
            organizationId
          })
        } catch (error) {
          this.throwIfNotServerError(error)
        } finally {
          this.isLoading = false
        }
      },
      synchronizeOrganizations: async function () {
        const vm = this
        try {
          vm.isLoading = true
          await vm.synchronizeOrganizationsAction()
          vm.$refs.paginatedTable.getDataFromServer()
        } catch (error) {
          vm.throwIfNotServerError(error)
        } finally {
          this.isLoading = false
        }
      }
    }
  }
</script>
